import { useEffect } from 'react'
import type { FC } from 'react'
import { useRhymeSearch } from '../../hooks/useRhymeSearch'
import { RhymeResultsDisplay } from './RhymeResultsDisplay'
import type { RhymeSearchResponse } from '../../types/wordTools'

interface RhymeSearchTabProps {
  /** Current search term to query */
  searchTerm: string
  /** Rhythmic stability for recommendation context */
  rhythmicStability?: number
  /** Search nonce to force re-search */
  searchNonce?: number
  /** Called when search is cleared */
  onClear: () => void
}

/**
 * Rhyme search tab content for exploration worksheet.
 *
 * Features:
 * - Loading skeleton (300ms minimum) for consistent UX
 * - Cached result indicator during background refresh
 * - Recommendation badges for stability-aware categories
 * - Friendly empty/error states with retry guidance
 * - Organized category display with syllable grouping
 *
 * Scope 06: parentWordId removed. Collection is flat words.
 */
export const RhymeSearchTab: FC<RhymeSearchTabProps> = ({
  searchTerm,
  rhythmicStability,
  searchNonce,
  onClear
}) => {
  const { results, isLoading, error, isCached, isStale, search, clearResults } = useRhymeSearch()

  // Convert rhythmicStability (0-1) to backend stability string
  // Slider 1 "Varied" → 0.0, Slider 10 "Rigid" → 1.0
  // 0.7-1.0 = stable (slider 8-10, rigid - expects perfect rhymes)
  // 0.3-0.7 = neutral (slider 4-7, mixed)
  // 0.0-0.3 = unstable (slider 1-3, varied - welcomes creative rhyming)
  const getStabilityString = (stability?: number): string | undefined => {
    if (stability === undefined) return undefined
    if (stability >= 0.7) return 'stable'
    if (stability <= 0.3) return 'unstable'
    return 'neutral'
  }

  // Trigger search when term, stability, or nonce changes
  useEffect(() => {
    console.log('[RhymeSearchTab] useEffect triggered:', {
      searchTerm,
      rhythmicStability,
      searchNonce,
      hasSearchFn: !!search,
      hasClearFn: !!clearResults
    })
    if (searchTerm.trim()) {
      const stabilityStr = getStabilityString(rhythmicStability)
      console.log('[RhymeSearchTab] Calling search:', { searchTerm, stabilityStr })
      search(searchTerm, stabilityStr)
    } else {
      clearResults()
    }
  }, [searchTerm, rhythmicStability, searchNonce, search, clearResults])

  // Loading skeleton - shows for at least 300ms
  if (isLoading && !isCached) {
    return (
      <div data-testid="rhyme-search-loading" className="space-y-4 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
        <div className="h-4 bg-gray-200 rounded w-2/3"></div>
        <div className="space-y-2">
          <div className="h-8 bg-gray-200 rounded"></div>
          <div className="h-8 bg-gray-200 rounded"></div>
        </div>
      </div>
    )
  }

  // Error state with retry button
  if (error && !results) {
    return (
      <div data-testid="rhyme-search-error" className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <span className="text-red-500 text-xl">⚠️</span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-1">
                Failed to load rhymes
              </h3>
              <p className="text-sm text-red-700 mb-3">
                {error}
              </p>
              <button
                data-testid="rhyme-search-retry"
                onClick={() => search(searchTerm, getStabilityString(rhythmicStability))}
                className="text-sm font-medium text-red-700 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-3">
          <p className="font-medium mb-1">Troubleshooting:</p>
          <ul className="list-disc list-inside space-y-1 text-gray-600">
            <li>Check your internet connection</li>
            <li>Try a different word</li>
            <li>Reload the page if the issue persists</li>
          </ul>
        </div>
      </div>
    )
  }

  // Empty state - no search term entered
  if (!searchTerm.trim()) {
    return (
      <div data-testid="rhyme-search-empty" className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">🔍</div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          Enter a word to find rhymes
        </h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto">
          Type a word in the search box above to explore rhyme patterns and recommendations.
        </p>
      </div>
    )
  }

  // No results state - searched but no rhymes found
  if (results && !hasAnyRhymes(results)) {
    return (
      <div data-testid="rhyme-search-no-results" className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">🤔</div>
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No rhymes found for &ldquo;{results.word}&rdquo;
        </h3>
        <p className="text-sm text-gray-500 max-w-xs mx-auto mb-4">
          {!results.pronunciation
            ? 'This word is not in the pronunciation dictionary.'
            : 'Try a different word or check the spelling.'}
        </p>
        <button
          onClick={onClear}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 underline"
        >
          Clear search
        </button>
      </div>
    )
  }

  // Results view with new component
  if (results) {
    return (
      <div data-testid="rhyme-search-results" className="space-y-4">
        {/* Cache/Stale indicator */}
        {(isCached || isStale) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-700">
            {isStale ? (
              <span>📡 Updating results...</span>
            ) : (
              <span>💾 Showing cached results</span>
            )}
          </div>
        )}

        {/* Word info */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-1">
          <div className="flex items-baseline gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{results.word}</h3>
            {results.pronunciation && (
              <span className="text-xs font-mono text-gray-500">
                {results.pronunciation}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="font-medium">Stability:</span>
            <span className={`px-2 py-0.5 rounded ${
              results.stability === 'stable'
                ? 'bg-green-100 text-green-700'
                : results.stability === 'unstable'
                ? 'bg-yellow-100 text-yellow-700'
                : 'bg-gray-100 text-gray-600'
            }`}>
              {results.stability}
            </span>
          </div>
        </div>

        {/* Results display with categorized rhymes */}
        <RhymeResultsDisplay
          results={results}
          searchWord={results.word}
        />

        {/* Performance metadata */}
        <div className="text-xs text-gray-400 pt-4 border-t">
          <p>Processing time: {results.processing_time_ms}ms</p>
        </div>
      </div>
    )
  }

  return null
}

/**
 * Check if results contain any rhymes across all categories.
 */
function hasAnyRhymes(results: RhymeSearchResponse): boolean {
  const { categories } = results

  // Check standard categories
  const standardCategories = [
    categories.perfect,
    categories.additive,
    categories.subtractive,
    categories.assonance,
    categories.consonance
  ]

  for (const category of standardCategories) {
    if (category.syllables_1?.length > 0 ||
        category.syllables_2?.length > 0 ||
        category.syllables_3?.length > 0 ||
        category.syllables_4_plus?.length > 0) {
      return true
    }
  }

  // Check family subcategories
  const family = categories.family
  const familySubcategories = [
    family.voiced_plosive,
    family.voiced_fricative,
    family.voiced_nasal,
    family.unvoiced_plosive,
    family.unvoiced_fricative
  ]

  for (const subcategory of familySubcategories) {
    if (subcategory.syllables_1?.length > 0 ||
        subcategory.syllables_2?.length > 0 ||
        subcategory.syllables_3?.length > 0 ||
        subcategory.syllables_4_plus?.length > 0) {
      return true
    }
  }

  return false
}
